import { websocketUrl } from '../config';

export type Frame = { t: string; id?: string; d?: any };

export type SocketStatus = 'idle' | 'connecting' | 'online' | 'offline' | 'unauthorized';

interface Options {
  getAccessToken: () => string | null;
  /**
   * Обновление токена. Возвращает null только когда сессия действительно
   * недействительна; при сетевом сбое должен бросать, а не сообщать об отзыве —
   * иначе обрыв связи будет выкидывать человека из аккаунта.
   */
  refreshAccessToken: () => Promise<string | null>;
  onFrame: (frame: Frame) => void;
  onStatus: (status: SocketStatus) => void;
  /** Вызывается после каждой успешной аутентификации — время догрузить пропущенное. */
  onAuthenticated: () => void;
}

const PING_INTERVAL_MS = 30_000;
/** Мобильные операторы и прокси рвут молчащие соединения примерно через минуту. */
const PONG_TIMEOUT_MS = 60_000;
const BACKOFF_START_MS = 1_000;
const BACKOFF_MAX_MS = 30_000;
const REQUEST_TIMEOUT_MS = 10_000;

/** Код закрытия «токен не годится» — договорённость с сервером. */
const CLOSE_UNAUTHORIZED = 4401;

export class Socket {
  private ws: WebSocket | null = null;
  private status: SocketStatus = 'idle';
  private authenticated = false;
  private closedByUs = false;

  private backoffMs = BACKOFF_START_MS;
  private reconnectTimer: number | null = null;
  private pingTimer: number | null = null;
  private lastPongAt = 0;
  private refreshAttempted = false;

  private nextRequestId = 1;
  private readonly pending = new Map<string, { resolve: (f: Frame) => void; reject: (e: Error) => void; timer: number }>();
  /** Кадры, отправленные до завершения аутентификации, ждут своей очереди. */
  private readonly queue: Frame[] = [];

  constructor(private readonly options: Options) {}

  connect(): void {
    this.closedByUs = false;
    if (this.ws && (this.ws.readyState === WebSocket.OPEN || this.ws.readyState === WebSocket.CONNECTING)) {
      return;
    }
    const token = this.options.getAccessToken();
    if (!token) {
      // Токена нет под рукой — это не отзыв сессии: хранилище могло ответить
      // не сразу, а приложение как раз просыпается. Пробуем ещё раз, а не
      // объявляем человека вышедшим.
      this.setStatus('offline');
      this.scheduleReconnect();
      return;
    }

    this.setStatus('connecting');
    const ws = new WebSocket(websocketUrl());
    this.ws = ws;

    ws.onopen = () => {
      this.authenticated = false;
      ws.send(JSON.stringify({ t: 'auth', id: 'auth', d: { accessToken: this.options.getAccessToken() } }));
    };

    ws.onmessage = (event) => {
      let frame: Frame;
      try {
        frame = JSON.parse(event.data as string) as Frame;
      } catch {
        return;
      }
      this.handleFrame(frame);
    };

    ws.onclose = (event) => {
      this.stopPing();
      this.rejectAllPending(new Error('соединение закрыто'));
      this.ws = null;
      this.authenticated = false;

      if (this.closedByUs) {
        this.setStatus('idle');
        return;
      }

      // Токен истёк: обновляем и сразу переподключаемся, не тратя backoff.
      // Одна попытка — иначе при отозванной сессии получится бесконечный цикл.
      if (event.code === CLOSE_UNAUTHORIZED && !this.refreshAttempted) {
        this.refreshAttempted = true;
        void this.options.refreshAccessToken().then(
          (token) => {
            if (token) {
              this.connect();
            } else {
              this.setStatus('unauthorized');
            }
          },
          () => {
            // Сеть подвела, а не сессия. Выкидывать человека из аккаунта нельзя:
            // тот же код закрытия приходит и когда клиент просто не успел
            // прислать кадр auth по медленной связи.
            this.refreshAttempted = false;
            this.setStatus('offline');
            this.scheduleReconnect();
          });
        return;
      }
      if (event.code === CLOSE_UNAUTHORIZED) {
        // Второй отказ подряд — не повод выходить из аккаунта.
        //
        // Признак «обновление уже пробовали» снимается только при успешном
        // подключении. Если после обновления токена соединение оборвалось
        // по сети — а за туннелем это обычное дело, — признак оставался
        // взведённым, и следующий истёкший токен клиент принимал за отзыв
        // сессии. Человека выбрасывало при том, что сервер ничего не отзывал.
        //
        // Из аккаунта выходим ровно в одном случае: сервер прямо отказал
        // в обновлении токена. Это решает refreshAccessToken выше.
        this.refreshAttempted = false;
        this.setStatus('offline');
        this.scheduleReconnect();
        return;
      }

      this.setStatus('offline');
      this.scheduleReconnect();
    };

    ws.onerror = () => {
      // Подробностей браузер не даёт; закрытие обработает onclose
    };
  }

  close(): void {
    this.closedByUs = true;
    this.clearReconnect();
    this.stopPing();
    this.ws?.close();
    this.ws = null;
    this.authenticated = false;
    this.setStatus('idle');
  }

  send(frame: Frame): void {
    if (this.authenticated && this.ws?.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify(frame));
    } else {
      this.queue.push(frame);
      // Соединения нет — попробуем поднять. Кадр уйдёт после аутентификации.
      if (!this.ws && !this.closedByUs) this.connect();
    }
  }

  /** Кадр с ответом: сервер вернёт тот же id. */
  request(t: string, d?: unknown): Promise<Frame> {
    const id = 'r' + this.nextRequestId++;
    return new Promise((resolve, reject) => {
      const timer = window.setTimeout(() => {
        this.pending.delete(id);
        reject(new Error(`нет ответа на ${t}`));
      }, REQUEST_TIMEOUT_MS);
      this.pending.set(id, { resolve, reject, timer });
      this.send({ t, id, d });
    });
  }

  private handleFrame(frame: Frame): void {
    if (frame.t === 'auth.ok') {
      this.authenticated = true;
      this.refreshAttempted = false;
      this.backoffMs = BACKOFF_START_MS;
      this.lastPongAt = Date.now();
      this.setStatus('online');
      this.startPing();
      this.flushQueue();
      // Сразу сообщаем состояние окна: соединение могло восстановиться,
      // пока приложение свёрнуто, и тогда уведомления по-прежнему нужны
      this.reportActivity(document.visibilityState === 'visible');
      this.options.onAuthenticated();
      return;
    }
    if (frame.t === 'auth.err') {
      this.setStatus('unauthorized');
      return;
    }
    if (frame.t === 'pong') {
      this.lastPongAt = Date.now();
    }

    if (frame.id) {
      const waiter = this.pending.get(frame.id);
      if (waiter) {
        this.pending.delete(frame.id);
        clearTimeout(waiter.timer);
        waiter.resolve(frame);
        // Ответ отдаём и в общий обработчик: состояние обновляется единообразно
      }
    }

    this.options.onFrame(frame);
  }

  private flushQueue(): void {
    while (this.queue.length > 0 && this.ws?.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify(this.queue.shift()));
    }
  }

  private startPing(): void {
    this.stopPing();
    this.pingTimer = window.setInterval(() => {
      if (this.ws?.readyState !== WebSocket.OPEN) return;

      // Молчание сервера дольше таймаута означает «соединение мертво, но
      // браузер об этом не знает» — типичная ситуация после сна устройства
      if (Date.now() - this.lastPongAt > PONG_TIMEOUT_MS) {
        this.ws.close();
        return;
      }
      this.ws.send(JSON.stringify({ t: 'ping' }));

      // Вместе с ударом сердца подтверждаем состояние окна. Сервер считает
      // отметку устаревшей через две минуты — без обновления он начал бы слать
      // уведомления человеку, который как раз смотрит в переписку и просто
      // не переключал вкладки.
      this.reportActivity(document.visibilityState === 'visible');
    }, PING_INTERVAL_MS);
  }

  private stopPing(): void {
    if (this.pingTimer !== null) {
      clearInterval(this.pingTimer);
      this.pingTimer = null;
    }
  }

  private scheduleReconnect(): void {
    this.clearReconnect();
    // Случайная добавка: иначе после перезапуска сервера все клиенты
    // ударят одновременно и уронят его снова
    const jitter = Math.random() * 0.3 * this.backoffMs;
    const delay = this.backoffMs + jitter;
    this.reconnectTimer = window.setTimeout(() => this.connect(), delay);
    this.backoffMs = Math.min(this.backoffMs * 2, BACKOFF_MAX_MS);
  }

  private clearReconnect(): void {
    if (this.reconnectTimer !== null) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
  }

  /**
   * Сообщает серверу, смотрит ли человек на приложение.
   * <p>
   * От этого зависит, отправлять ли уведомление: свёрнутое окно соединение
   * не закрывает, и без этого признака сервер считал бы человека «на связи»
   * и уведомление не присылал.
   */
  reportActivity(active: boolean): void {
    if (this.authenticated && this.ws?.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify({ t: 'state', d: { active } }));
    }
  }

  /** Возврат на вкладку — повод проверить связь немедленно, не дожидаясь backoff. */
  wakeUp(): void {
    if (this.closedByUs) return;
    if (this.ws?.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify({ t: 'ping' }));
      return;
    }
    this.backoffMs = BACKOFF_START_MS;
    this.clearReconnect();
    this.connect();
  }

  private rejectAllPending(error: Error): void {
    for (const [, waiter] of this.pending) {
      clearTimeout(waiter.timer);
      waiter.reject(error);
    }
    this.pending.clear();
  }

  private setStatus(status: SocketStatus): void {
    if (this.status !== status) {
      this.status = status;
      this.options.onStatus(status);
    }
  }
}
